/**
 * StackedColumns — 6-month composition (spec §6.2) and the 6-month card schedule (§6.6).
 * One y-axis, ≤ 24px columns with a 4px-rounded data end, 2px gaps between segments, a
 * mandatory legend for ≥ 2 series, the total above every column and the current (partial)
 * column dimmed with "עד כה".
 */

import { Figure, type LegendItem, type TableTwin } from './Figure.tsx';
import { ChartTooltip, useTooltip, type TipContent } from './Tooltip.tsx';
import { axisLabel, bandScale, ticksFor, topRound } from './chart-utils.ts';
import { useChartWidth } from './useChartWidth.ts';

export interface StackSeries {
  key: string;
  label: string;
  /** 1..6 — the fixed categorical order of `--series-N`. */
  series: number;
  hatch?: boolean;
  dim?: boolean;
}

export interface StackRow {
  key: string;
  /** X axis label. */
  label: string;
  /** Accessible row name. */
  aria: string;
  values: Record<string, number>;
  tip: TipContent;
}

export function StackedColumns({
  question,
  period,
  summary,
  series,
  rows,
  currentIndex = -1,
  step,
  height = 280,
  approxTotals,
  table,
  onSegmentClick,
}: {
  question: string;
  period?: string;
  summary?: string;
  series: StackSeries[];
  rows: StackRow[];
  currentIndex?: number;
  step?: number;
  height?: number;
  approxTotals?: boolean;
  table?: TableTwin;
  onSegmentClick?: (seriesKey: string) => void;
}) {
  const [ref, width] = useChartWidth(720);
  const tooltip = useTooltip();
  const patternId = `hatch-${series.map((s) => s.key).join('-')}`;

  const ml = 56;
  const mr = 12;
  const mt = 26;
  const mb = 30;
  const totals = rows.map((row) => series.reduce((sum, s) => sum + (row.values[s.key] ?? 0), 0));
  const ticks = ticksFor(Math.max(...totals, 1) * 1.02, 6, step);
  const { band, center } = bandScale(rows.length, ml, width - mr);
  const barWidth = Math.min(24, Math.floor(band * 0.5));
  const py = (value: number) => mt + ((ticks.top - value) / ticks.top) * (height - mt - mb);

  const legend: LegendItem[] = series.map((s) => ({
    label: s.label,
    color: `var(--series-${s.series})`,
    hatch: s.hatch,
    dim: s.dim,
  }));

  return (
    <div ref={ref}>
      <Figure question={question} period={period} summary={summary} legend={legend} table={table}>
        <svg viewBox={`0 0 ${width} ${height}`}>
          <defs>
            <pattern id={patternId} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="6" stroke="var(--chart-surface)" strokeWidth="2" />
            </pattern>
          </defs>
          {ticks.ticks.map((tick) => (
            <g key={tick}>
              <line className="grid" x1={ml} x2={width - mr} y1={py(tick).toFixed(1)} y2={py(tick).toFixed(1)} />
              <text x={ml - 8} y={(py(tick) + 4).toFixed(1)} textAnchor="end">
                {axisLabel(tick)}
              </text>
            </g>
          ))}
          <line className="axis" x1={ml} x2={width - mr} y1={py(0).toFixed(1)} y2={py(0).toFixed(1)} />
          {rows.map((row, rowIndex) => {
            const cx = center(rowIndex);
            const total = totals[rowIndex] ?? 0;
            let acc = 0;
            const handlers = tooltip.bind(row.tip);
            return (
              <g key={row.key}>
                {series.map((s) => {
                  const value = row.values[s.key] ?? 0;
                  if (value <= 0) return null;
                  const y = py(acc + value);
                  const rawHeight = py(acc) - py(acc + value);
                  const gap = acc > 0 ? 2 : 0;
                  const h = Math.max(0, rawHeight - gap);
                  const isTop = acc + value >= total - 0.01;
                  const x0 = cx - barWidth / 2;
                  const opacity = currentIndex === rowIndex ? (s.dim ? 0.35 : 0.6) : s.dim ? 0.5 : 1;
                  acc += value;
                  const shape = isTop ? (
                    <path d={topRound(x0, y + gap, barWidth, h, 4)} className={`s${s.series}`} opacity={opacity} />
                  ) : (
                    <rect x={x0.toFixed(1)} y={(y + gap).toFixed(1)} width={barWidth} height={h.toFixed(1)} className={`s${s.series}`} opacity={opacity} />
                  );
                  return (
                    <g
                      key={s.key}
                      className="seg"
                      tabIndex={0}
                      role="img"
                      aria-label={`${row.aria} · ${s.label}`}
                      onClick={onSegmentClick ? () => onSegmentClick(s.key) : undefined}
                      onKeyDown={
                        onSegmentClick
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onSegmentClick(s.key);
                              }
                            }
                          : undefined
                      }
                      style={onSegmentClick ? { cursor: 'pointer' } : undefined}
                      {...handlers}
                    >
                      {shape}
                      {s.hatch ? (
                        isTop ? (
                          <path d={topRound(x0, y + gap, barWidth, h, 4)} fill={`url(#${patternId})`} opacity={0.7} />
                        ) : (
                          <rect x={x0.toFixed(1)} y={(y + gap).toFixed(1)} width={barWidth} height={h.toFixed(1)} fill={`url(#${patternId})`} opacity={0.7} />
                        )
                      ) : null}
                    </g>
                  );
                })}
                <text x={cx.toFixed(1)} y={(py(total) - 6).toFixed(1)} textAnchor="middle" className="strong">
                  {(approxTotals ? '≈ ' : '') + axisLabel(Math.round(total))}
                </text>
                <text x={cx.toFixed(1)} y={height - 8} textAnchor="middle" className={currentIndex === rowIndex ? 't1' : undefined}>
                  {row.label}
                  {currentIndex === rowIndex ? ' · עד כה' : ''}
                </text>
              </g>
            );
          })}
        </svg>
      </Figure>
      <ChartTooltip state={tooltip.node} />
    </div>
  );
}
